import * as Sentry from "@sentry/react";
import { ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";

interface SentryErrorBoundaryProps {
  children: ReactNode;
  fallback?: Sentry.FallbackRender;
}

const ErrorFallback: Sentry.FallbackRender = ({ error, componentStack, eventId, resetError }) => {
  const isDevelopment = import.meta.env.VITE_SENTRY_ENVIRONMENT === 'dev';
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-lg lg:max-w-5xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 text-destructive">
            <AlertTriangle className="w-full h-full" />
          </div>
          <CardTitle>
            {isDevelopment ? "🧪 Sentry Error Boundary Triggered!" : "Something went wrong"}
          </CardTitle>
          <CardDescription>
            {isDevelopment 
              ? "This is the Sentry error boundary working correctly in development mode."
              : "We've been notified about this error and will fix it soon."
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isDevelopment && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
              <div className="font-medium text-green-800 mb-1">✅ Success!</div>
              <div className="text-green-700">
                The error was caught by Sentry's error boundary. In production, this would be logged to your Sentry dashboard.
              </div>
            </div>
          )}
          
          <details open className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Error details
            </summary>
            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-32">
              {error instanceof Error ? error.message : String(error)}
              {isDevelopment && error instanceof Error && error.stack && (
                <>
                  {'\n\nStack trace:\n'}
                  {error.stack}
                </>
              )}
              {isDevelopment && componentStack && (
                <>
                  {'\n\n\n\n\n\nComponent stack:\n'}
                  {componentStack}
                </>
              )}
              {isDevelopment && eventId && (
                <>
                  {'\n\nSentry Event ID: '}
                  {eventId}
                </>
              )}
            </pre>
          </details>
          
          <div className="flex gap-2">
            <Button onClick={resetError} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset & Try Again
            </Button>
            {isDevelopment && (
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                className="flex-1"
              >
                Reload Page
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const SentryErrorBoundary = ({ children, fallback }: SentryErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary fallback={fallback || ErrorFallback} showDialog>
      {children}
    </Sentry.ErrorBoundary>
  );
}; 